import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import conf from "config-dlm";
import ErrorChip from "components/utils/ErrorChip";
import JsonModal from "components/utils/Json";
import VirtualChip from "components/utils/VirtualChip";
import { isFound, useSmartContent } from "components/utils/SmartCell";
import {
  LogicalDiskInfoSchemaBase,
  Snapshot,
  ProjectConfigSchema,
  PhysicalDiskInfoSchemaBase,
} from "hooks/pavm/base";

const ResourceRow = (props: {
  open: boolean;
  data: Snapshot["resources"];
  config: ProjectConfigSchema;
}) => {
  const {
    open,
    config: { host, hardware },
    data: { diskLogical, diskPhysical, memory, cpuUsage, cpuTemperature },
  } = props;

  const virtual = hardware === "virtual";
  const CPUTemperature = virtual
    ? VirtualChip(cpuTemperature?.data)
    : useSmartContent(cpuTemperature?.data);

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          theme.palette.mode === "light" ? "primary.light" : "primary.dark",
      }}
    >
      <TableCell colSpan={10}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Table size="small" aria-label="disk-info">
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ color: "text.secondary" }}>
                  <b>CPU</b>
                </TableCell>
                {conf.COLUMNS.CPU.map((column: string) => (
                  <TableCell key={column} align="right">
                    <b>{column}</b>
                  </TableCell>
                ))}

                <TableCell align="right" sx={{ color: "text.secondary" }}>
                  <b>Memory (MB)</b>
                </TableCell>
                {conf.COLUMNS.MEMORY.map((column: string) => (
                  <TableCell key={column} align="right">
                    <b>{column}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow
                key={`${host}-cpu-&-memory`}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell />
                <TableCell align="right">
                  {isFound(cpuUsage?.data) ? (
                    cpuUsage?.data ?? "--"
                  ) : (
                    <ErrorChip
                      size="small"
                      info={cpuUsage?.error ?? "No data found"}
                    />
                  )}
                </TableCell>

                <TableCell align="right">
                  {isFound(cpuTemperature?.data) ? (
                    CPUTemperature
                  ) : (
                    <ErrorChip
                      size="small"
                      info={cpuTemperature?.error ?? "No data found"}
                    />
                  )}
                </TableCell>

                {/* <TableCell align="right">
                  <Button size="small" variant="outlined">
                    View
                  </Button>
                </TableCell> */}

                {!!memory?.data ? (
                  <TableCell />
                ) : (
                  <TableCell align="right">
                    <ErrorChip
                      size="small"
                      info={memory?.error ?? "No memory data found"}
                    />
                  </TableCell>
                )}

                <TableCell align="right">
                  {memory?.data?.available ?? "--"}
                </TableCell>
                <TableCell align="right">
                  {memory?.data?.free ?? "--"}
                </TableCell>
                <TableCell align="right">
                  {memory?.data?.total ?? "--"}
                </TableCell>
                {/* <TableCell align="right">
                  <Button size="small" variant="contained">
                    View
                  </Button>
                </TableCell> */}
              </TableRow>
            </TableBody>
          </Table>
        
        {!virtual && <Table size="small" aria-label="disk-info">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "text.secondary" }}>
                  <b>Physical Drives</b>
                </TableCell>
                {conf.COLUMNS.PHYSICAL_DISK.map((column: string) => (
                  <TableCell key={column} align="right">
                    <b>{column}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {<TableBody>
              {!!diskPhysical?.data && !!diskPhysical?.data.core ? (
                diskPhysical?.data?.core.map((data: PhysicalDiskInfoSchemaBase, index: number) => (
                  <TableRow
                    key={`${host}-${data?.serialNumber}`}
                    sx={conf.LAST_CHILD_NO_BORDER}
                  >
                    <TableCell />
                    <TableCell align="right">{data?.model ?? "--"}</TableCell>
                    <TableCell align="right">{useSmartContent(data?.serialNumber)}</TableCell>
                    <TableCell align="right">{data?.firmware ?? "--"}</TableCell>
                    <TableCell align="right">{data?.totalSize ?? "--"}</TableCell>
                    <TableCell align="right">
                      {data?.powerOnHours ?? "--"}
                    </TableCell>
                    <TableCell align="right">
                      {data?.powerCycleCount ?? "--"}
                    </TableCell>
           {         <TableCell align="right">
                      <JsonModal
                        typeTitle={data ? `Device: ${data?.serialNumber}` : undefined}
                        type="Disk"
                        data={diskPhysical.data.raw[index]}
                        title={host}
                      />
                    </TableCell>}
                  </TableRow>
                ))
              ) : (
                <TableRow
                  key={`${host}-disk-empty`}
                  sx={conf.LAST_CHILD_NO_BORDER}
                >
                  <TableCell>
                    <ErrorChip
                      size="small"
                      info={diskPhysical?.error ?? "No disk data found"}
                    />
                  </TableCell>
                  {conf.COLUMNS.PHYSICAL_DISK.map((column: string) => (
                    <TableCell key={column} align="right">
                      --
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>}
        </Table>}


          <Table size="small" aria-label="disk-info">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "text.secondary" }}>
                  <b>Logical Volumes</b>
                </TableCell>
                {conf.COLUMNS.LOGICAL_DISK.map((column: string) => (
                  <TableCell key={column} align="right">
                    <b>{column}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!!diskLogical?.data ? (
                diskLogical?.data?.core.map((data: LogicalDiskInfoSchemaBase) => (
                  <TableRow
                    key={`${host}-${data?.filesystem}`}
                    sx={conf.LAST_CHILD_NO_BORDER}
                  >
                    <TableCell />
                    <TableCell align="right">
                      {data?.filesystem ?? "--"}
                    </TableCell>
                    <TableCell align="right">{data?.size ?? "--"}</TableCell>
                    <TableCell align="right">{data?.used ?? "--"}</TableCell>
                    <TableCell align="right">{data?.avail ?? "--"}</TableCell>
                    <TableCell align="right">{data?.use ?? "--"}</TableCell>
                    <TableCell align="right">
                      {data?.mountedOn ?? "--"}
                    </TableCell>
                    <TableCell align="right">
                      <JsonModal
                        type="Disk"
                        data={diskLogical.data.raw}
                        title={host}
                      />
                    </TableCell>
                    {/* <TableCell align="right">
                      <Button size="small" variant="contained">
                        View
                      </Button>
                    </TableCell> */}
                  </TableRow>
                ))
              ) : (
                <TableRow
                  key={`${host}-disk-empty`}
                  sx={conf.LAST_CHILD_NO_BORDER}
                >
                  <TableCell>
                    <ErrorChip
                      size="small"
                      info={diskLogical?.error ?? "No disk data found"}
                    />
                  </TableCell>
                  {conf.COLUMNS.LOGICAL_DISK.map((column: string) => (
                    <TableCell key={column} align="right">
                      --
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default ResourceRow;
